// screens/staff/ClassPulseScreen.tsx
//
// W6 v1.1 Session 1 — class pulse per spec §5.5.
//
// Visible to titolare + instructor_*; segreteria walled at <RequireStaffRole>
// in StaffApp + on the BE via requireStaffRole(...).
//
// Layout (desktop-first):
//   - Header + Italian "Lei" intro
//   - Planning hint callout (when present)
//   - Period toggle 7gg / 30gg / 90gg (shared <PeriodToggle />)
//   - Heatmap: rows = argomenti (Italian Patente B sort_order), columns =
//     cohorts. Color scale red→yellow→green by mastery_avg. Each cell shows
//     the rounded percent + a 3-tier text label ("Forte" / "Medio" / "Debole")
//     so users with color-vision deficiency get a non-color cue (WCAG 1.4.1).
//   - Outliers panel (top 5 most extreme)
//   - Loading + error + empty states with Italian copy

import { useEffect, useState, type JSX } from 'react';
import { useTokens } from '../../state/theme';
import {
  staffApi,
  type PulsePayload,
  type PulseHeatmapRow,
} from '../../api/staff';
import PeriodToggle, { type Period } from './PeriodToggle';

const PERIOD_TO_QUERY: Record<Period, '7d' | '30d' | '90d'> = { 7: '7d', 30: '30d', 90: '90d' };

// 3-tier text label thresholds — match the 8-factor weight calculator's
// red/yellow/green spec.
function masteryTextLabel(score: number): { label: string; color: 'green' | 'yellow' | 'red' } {
  if (score >= 0.7) return { label: 'Forte',  color: 'green' };
  if (score >= 0.4) return { label: 'Medio',  color: 'yellow' };
  return { label: 'Debole', color: 'red' };
}

export default function ClassPulseScreen(): JSX.Element {
  const tokens = useTokens();
  const [days, setDays] = useState<Period>(30);
  const [data, setData] = useState<PulsePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setData(null);
    staffApi.pulse(PERIOD_TO_QUERY[days])
      .then((p) => {
        if (!alive) return;
        setData(p);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setError('Non è stato possibile caricare il polso classe. Riprovi tra poco.');
        setLoading(false);
      });
    return () => { alive = false; };
  }, [days]);

  const isEmpty = !loading && !error && data
    && data.heatmap.length === 0
    && data.weekly_deltas.length === 0
    && data.outliers.length === 0;

  return (
    <section data-testid="class-pulse-screen">
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: tokens.fonts.serif, fontSize: 26, fontWeight: 700, margin: 0 }}>
          Polso classe
        </h1>
        <p style={{ color: tokens.textMuted, margin: '4px 0 0 0', fontSize: 13 }}>
          Andamento della padronanza per argomento e per coorte. Le cifre indicano
          la mastery media; il colore e l'etichetta segnalano l'intensità.
        </p>
      </header>

      {/* Planning hint */}
      {!loading && !error && data?.planning_hint && (
        <aside
          data-testid="pulse-planning-hint"
          style={{
            background:    tokens.accentSoft,
            border:        `1px solid ${tokens.line}`,
            borderRadius:  tokens.radius.lg,
            padding:       '14px 18px',
            marginBottom:  20,
            color:         tokens.text,
            fontSize:      14,
            lineHeight:    1.5,
          }}
        >
          <span style={{ fontWeight: 600, color: tokens.accentInk, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase' }}>
            Suggerimento
          </span>
          <p style={{ margin: '6px 0 0 0' }}>{data.planning_hint}</p>
        </aside>
      )}

      {/* Period toggle */}
      <div style={{ marginBottom: 24 }}>
        <PeriodToggle value={days} onChange={setDays} testidPrefix="pulse-period" />
      </div>

      {error && (
        <div role="alert" data-testid="class-pulse-error" style={{ marginBottom: 16, color: tokens.wrong, fontSize: 13 }}>
          {error}
        </div>
      )}

      {loading && (
        <div data-testid="class-pulse-loading" style={{ color: tokens.textMuted, fontSize: 13 }}>
          Caricamento…
        </div>
      )}

      {isEmpty && (
        <div data-testid="class-pulse-empty" style={{ color: tokens.textMuted, fontSize: 14 }}>
          Non ci sono ancora dati di profitto per questo periodo.
        </div>
      )}

      {!loading && !error && !isEmpty && data && (
        <>
          <Heatmap data={data} />
          <Outliers data={data} />
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Heatmap
// ---------------------------------------------------------------------------

function Heatmap({ data }: { data: PulsePayload }): JSX.Element {
  const tokens = useTokens();
  // Cohorts come from the first row's cells (every row has the same cohort
  // set; argomenti × cohorts is a complete grid).
  const cohorts = data.heatmap[0]?.cells.map(c => ({ id: c.cohort_id, label: c.cohort_label })) ?? [];

  if (data.heatmap.length === 0 || cohorts.length === 0) {
    return (
      <div data-testid="pulse-heatmap-empty" style={{ color: tokens.textMuted, fontSize: 13 }}>
        Nessun argomento con dati sufficienti nel periodo selezionato.
      </div>
    );
  }

  return (
    <section data-testid="pulse-heatmap" style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: tokens.textMuted, margin: '0 0 12px 0', textTransform: 'uppercase' }}>
        Mappa argomenti × coorti
      </h2>
      <div style={{ overflowX: 'auto' }}>
        <table
          data-testid="pulse-heatmap-table"
          style={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            width: 'max-content',
            minWidth: '100%',
            fontSize: 13,
          }}
        >
          <thead>
            <tr>
              <th
                scope="col"
                style={{
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontWeight: 600,
                  color: tokens.textMuted,
                  borderBottom: `1px solid ${tokens.line}`,
                  position: 'sticky',
                  left: 0,
                  background: tokens.bg,
                  zIndex: 1,
                }}
              >
                Argomento
              </th>
              {cohorts.map(c => (
                <th
                  key={c.id}
                  scope="col"
                  data-testid={`pulse-heatmap-col-${c.id}`}
                  style={{
                    textAlign: 'center',
                    padding: '8px 10px',
                    fontWeight: 600,
                    color: tokens.text,
                    borderBottom: `1px solid ${tokens.line}`,
                    minWidth: 110,
                  }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.heatmap.map((row, idx) => (
              <HeatmapRow key={row.argomento_id} row={row} striped={idx % 2 === 0} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function HeatmapRow({ row, striped }: { row: PulseHeatmapRow; striped: boolean }): JSX.Element {
  const tokens = useTokens();
  return (
    <tr style={{ background: striped ? tokens.surface : 'transparent' }}>
      <th
        scope="row"
        data-testid={`pulse-heatmap-row-${row.argomento_id}`}
        style={{
          textAlign: 'left',
          padding: '10px 12px',
          fontWeight: 500,
          color: tokens.text,
          borderBottom: `1px solid ${tokens.line}`,
          position: 'sticky',
          left: 0,
          background: striped ? tokens.surface : tokens.bg,
          zIndex: 1,
          maxWidth: 240,
        }}
      >
        {row.argomento_name}
      </th>
      {row.cells.map(cell => {
        const empty = cell.sample_size === 0;
        const { label, color } = masteryTextLabel(cell.mastery_avg);
        const colorMap: Record<typeof color, string> = {
          green:  tokens.right,
          yellow: tokens.accent,
          red:    tokens.wrong,
        };
        const softMap: Record<typeof color, string> = {
          green:  tokens.rightSoft,
          yellow: tokens.accentSoft,
          red:    tokens.wrongSoft,
        };
        return (
          <td
            key={cell.cohort_id}
            data-testid={`pulse-cell-${row.argomento_id}-${cell.cohort_id}`}
            aria-label={
              empty
                ? `${row.argomento_name}, ${cell.cohort_label}: dati insufficienti.`
                : `${row.argomento_name}, ${cell.cohort_label}: ${(cell.mastery_avg * 100).toFixed(0)}%, ${label}.`
            }
            style={{
              textAlign: 'center',
              padding: '10px 12px',
              borderBottom: `1px solid ${tokens.line}`,
              background: empty ? 'transparent' : softMap[color],
              color: empty ? tokens.textFaint : colorMap[color],
              fontWeight: 600,
              minWidth: 110,
            }}
          >
            {empty ? (
              <span aria-hidden="true">—</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <span>{(cell.mastery_avg * 100).toFixed(0)}%</span>
                <span aria-hidden="true" style={{ fontSize: 11, fontWeight: 500, color: colorMap[color] }}>
                  {label}
                </span>
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Outliers panel
// ---------------------------------------------------------------------------

function Outliers({ data }: { data: PulsePayload }): JSX.Element | null {
  const tokens = useTokens();
  if (data.outliers.length === 0) return null;
  return (
    <section data-testid="pulse-outliers" style={{ marginTop: 16 }}>
      <h2 style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, color: tokens.textMuted, margin: '0 0 12px 0', textTransform: 'uppercase' }}>
        Coorti fuori scala
      </h2>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.outliers.map(o => {
          const positive = o.z_score > 0;
          const arrow = positive ? '↑' : '↓';
          const arrowColor = positive ? tokens.right : tokens.wrong;
          return (
            <li
              key={`${o.argomento_id}-${o.cohort_id}`}
              data-testid={`pulse-outlier-${o.argomento_id}-${o.cohort_id}`}
              style={{
                background:    tokens.surface,
                border:        `1px solid ${tokens.line}`,
                borderRadius:  tokens.radius.md,
                padding:       '10px 14px',
                fontSize:      13,
                color:         tokens.text,
                display:       'flex',
                alignItems:    'center',
                gap:           10,
              }}
            >
              <span aria-hidden="true" style={{ color: arrowColor, fontWeight: 700, fontSize: 16 }}>{arrow}</span>
              <span>
                <strong>{o.cohort_label}</strong> su <strong>{o.argomento_name}</strong>:{' '}
                {(o.mastery_avg * 100).toFixed(0)}% (mediana di scuola {(o.median_in_arg * 100).toFixed(0)}%)
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
